import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { classNames, responsiveVariables } from "../../utils/helpers";
import { onNextFrame } from "../../utils/_private/animation";
import { trapFocus } from "../../utils/_private/a11y";
import { Notification } from './Notification';
import { NotificationContext } from './Notification.context';
import { timeouts } from './Notification.constants';
import s from './Notification.module.css';
export const NotificationContainer = (props) => {
    const { notificationProps, id, status, inspected, index, type } = props;
    const { timeout = 'short', width } = notificationProps;
    const { show, hide, remove } = React.useContext(NotificationContext);
    const [notificationHeight, setNotificationHeight] = React.useState();
    const timeoutRef = React.useRef();
    const resizingRef = React.useRef(false);
    const trapFocusRef = React.useRef(null);
    const wrapperRef = React.useRef(null);
    const visible = status === 'entered';
    const containerClassNames = classNames(s.container, index === 0 && s[`--index-${index}`], type && s[`--type-${type}`], !inspected && (index === 1 || index === 2) && s[`--index-${index}`], !inspected && index >= 3 && s['--index-overflow']);
    const stopTimer = React.useCallback(() => {
        if (!timeoutRef.current)
            return;
        clearTimeout(timeoutRef.current);
    }, []);
    const startTimer = React.useCallback(() => {
        stopTimer();
        const timeoutValue = typeof timeout === 'string' ? timeouts[timeout] : timeout;
        if (timeout === 0)
            return;
        timeoutRef.current = setTimeout(() => {
            hide(id);
        }, timeoutValue !== null && timeoutValue !== void 0 ? timeoutValue : timeouts.short);
    }, [hide, id, timeout, stopTimer]);
    const handleTransitionEnd = (e) => {
        if (e.propertyName !== 'height')
            return;
        if (visible)
            return;
        remove(id);
    };
    React.useEffect(() => {
        if (wrapperRef.current) {
            setNotificationHeight(wrapperRef.current.clientHeight);
        }
        show(id);
        startTimer();
    }, [show, id, startTimer]);
    React.useEffect(() => {
        if (!wrapperRef.current)
            return;
        if (visible) {
            trapFocusRef.current = trapFocus(wrapperRef.current, {
                includeTrigger: true,
                mode: 'content-menu',
            });
        }
        else if (trapFocusRef.current) {
            trapFocusRef.current();
            trapFocusRef.current = null;
        }
    }, [visible]);
    React.useEffect(() => {
        if (!visible || index > 0)
            return;
        const handleResize = () => {
            resizingRef.current = true;
            onNextFrame(() => {
                resizingRef.current = false;
            });
            if (wrapperRef.current) {
                setNotificationHeight(wrapperRef.current.clientHeight);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [visible, index]);
    return (_jsx("li", { className: containerClassNames, style: Object.assign({ height: status === 'entered' ? `calc(${notificationHeight}px + var(--st-unit-2)` : 0, 
            // Disable transition when height of the notification can change
            transitionDuration: resizingRef.current ? '0s' : undefined }, responsiveVariables('--st-_w', width)), onTransitionEnd: handleTransitionEnd, onFocus: stopTimer, onBlur: startTimer, children: _jsx("span", { className: s.wrapper, children: _jsx(Notification, Object.assign({}, notificationProps, { isToast: true, attributes: { ref: wrapperRef } })) }) }));
};
